<template>
    <div class="like">
        <LikeIcon
            @click.native.prevent
            @handleLike="handleLike"
            :isLiked="isLiked"
            :animate="animate"
            :disableLikeAction="disableLikeAction"/>
        <div class="likesWrapper">
            <div class="likedBy"
                v-if="mentionedUser.name">
                <span class="regular space">
                    <template v-if="lastID === auth.id">
                        {{ siteContent.likedByYou }}
                    </template>
                    <template v-else>
                        {{ siteContent.likedBy }}
                    </template>
                </span>
                <router-link class="likeLinkStyle space"
                    :to="'/creator/' + mentionedUser.slug">
                    {{ mentionedUser.name }}
                </router-link>
                <div v-if="likesLength > 1">
                    <span class="regular space">
                        {{ siteContent.and }}
                    </span>
                    <span class="likeLinkStyle"
                        @click="handleUserList"
                        tabindex="0">
                        {{ likesLength - 1 }}
                        <template v-if="likesLength > 2">
                            {{ siteContent.others }}
                        </template>
                        <template v-else>
                            {{ siteContent.other }}
                        </template>
                    </span>
                </div>
            </div>
            <template v-else>
                <span class="regular">
                    {{ siteContent.beTheFirstToLike }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
    import { db } from '@/firebase'
    import { mapActions } from 'vuex'
    import firebase from 'firebase/app'

    import LikeIcon from '@/components/templates/LikeIcon'

    export default {
        name: 'LikeButton',
        components: {
            LikeIcon
        },
        props: {
            entry: {
                type: Object,
                required: true
            },
            likedBy: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                disableLikeAction: false,
                mentionedUser: {},
                animate: false
            }
        },
        computed: {
            isLiked() {
                if(this.auth.id) {
                    return this.likedBy.includes(this.auth.id)
                } else {
                    return false
                }
            },
            likeButton() {
                if(this.isLiked) {
                    return {
                        icon: 'favorite'
                    }
                } else {
                    return {
                        icon: 'favorite_border'
                    }
                }
            },
            likesLength() {
                return this.likedBy.length
            },
            lastID() {
                return this.likedBy[this.likesLength - 1]
            }
        },
        mounted() {
            if(this.likesLength > 0) {
                if(this.lastID === this.auth.id) {
                    let user = {
                        name: this.siteContent.you,
                        slug: this.auth.slug
                    }
                    this.mentionedUser = user
                } else {
                    this.fetchUser(this.lastID)
                }
            }
        },
        methods: {
            ...mapActions('notifications',['createNotification']),
            ...mapActions('users', ['setUserListDialog']),

            async handleLike() {
                if(!this.auth.id) {
                    this.$router.push('/auth')
                } else if(this.isLiked) {
                    // Remove from entry likes
                    try {
                        this.disableLikeAction = true
                        await db
                            .collection('entries')
                            .doc(this.entry.id)
                            .collection('social')
                            .doc('likedBy')
                            .update({
                                users: firebase
                                    .firestore
                                    .FieldValue
                                    .arrayRemove(this.auth.id)
                            })
                        this.disableLikeAction = false
                    } catch(error) {
                        throw error
                    }
                } else {
                    // Add to entry likes
                    try {
                        this.disableLikeAction = true
                        this.animate = true
                        setTimeout(() => {
                            this.animate = false
                        }, 750)
                        await db
                            .collection('entries')
                            .doc(this.entry.id)
                            .collection('social')
                            .doc('likedBy')
                            .update({
                                users: firebase
                                    .firestore
                                    .FieldValue
                                    .arrayUnion(this.auth.id)
                            })
                        this.disableLikeAction = false
                    } catch(error) {
                        throw error
                    }

                    // Create notification if not own entry
                    if(this.entry.creator.artist.id !== this.auth.id) {
                        try {
                            var notification = {
                                type:       'newLike',
                                docID:       this.entry.id,
                                title:       this.entry.content.title,
                                createdBy:   this.auth.id,
                                timestamp:   new Date(),
                                recipients:  [ this.entry.creator.artist.id ]
                            }

                            await this.createNotification({
                                notification: notification
                            })
                        } catch(error) {
                            throw error
                        }
                    }
                }
            },

            async fetchUser(id) {
                try {
                    let userRef = await db
                        .collection('users')
                        .doc(id)
                        .get()
                
                    this.mentionedUser = userRef.data()
                } catch(error) {
                    throw error
                }
            },

            handleUserList(e) {
                let dialog = {
                    isVisible: true,
                    users: this.likedBy
                }

                e.preventDefault()
                this.setUserListDialog(dialog)
            }
        },
        watch: {
            lastID() {
                if(this.lastID === this.auth.id) {
                    let user = {
                        name: this.siteContent.you,
                        slug: this.auth.slug
                    }
                    this.mentionedUser = user
                } else if(this.likesLength > 0) {
                    this.fetchUser(this.lastID)
                } else {
                    this.mentionedUser = {}
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../scss/variables';

    .like {
        display: flex;
        align-items: center;
    }

    .likesWrapper {
        font-family: $font-secondary;
        font-size: 0.875rem;
        font-weight: 700;
        line-height: 1.5rem;
        margin-left: 0.75rem;
    }

    .likedBy {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    .regular {
        font-weight: 500;
    }

    .space {
        margin-right: 0.3rem;
    }

    .likeLinkStyle {
        color: var(--color-black);
        padding: 0.25rem 0;

        &:hover {
            text-decoration: underline;
        }

        &:focus {
            box-shadow: var(--shadow-focus-s);
            outline: 0;
            border-radius: 0.5rem;
        }
    }
</style>